import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, Select, Spin, Table, Alert, Statistic, message, Grid } from 'antd'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { RobotOutlined, AlertOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { dashboardAPI, AIvsRulesComparison, RejectionReason, ValidationStepPerformance } from '../services/api'

const { Title, Text } = Typography
const { Option } = Select
const { useBreakpoint } = Grid

const Analytics: React.FC = () => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState(30)
  const [aiVsRules, setAiVsRules] = useState<AIvsRulesComparison | null>(null)
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([])
  const [validationPerformance, setValidationPerformance] = useState<ValidationStepPerformance>({})

  const loadAnalyticsData = async () => {
    try {
      setLoading(true)
      const [aiVsRulesData, rejectionData, validationData] = await Promise.all([
        dashboardAPI.getAIvsRulesComparison(selectedPeriod),
        dashboardAPI.getTopRejectionReasons(selectedPeriod),
        dashboardAPI.getValidationStepPerformance(selectedPeriod)
      ])

      setAiVsRules(aiVsRulesData)
      setRejectionReasons(rejectionData.top_reasons)
      setValidationPerformance(validationData.validation_steps)
    } catch (error) {
      message.error('Error al cargar los datos de análisis')
      console.error('Analytics load error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAnalyticsData()
  }, [selectedPeriod])

  const validationChartData = Object.entries(validationPerformance).map(([step, data]) => ({
    step: step.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    passed: data.passed,
    failed: data.failed,
    warning: data.warning,
    passRate: data.pass_rate
  }))

  const disagreementColumns = [
    {
      title: 'ID Simulación',
      dataIndex: 'simulation_id',
      key: 'simulation_id',
      width: 120,
    },
    {
      title: 'Decisión Final',
      dataIndex: 'final_decision',
      key: 'final_decision',
      width: 120,
      render: (status: string) => (
        <span className={`status-${status?.toLowerCase() || 'unknown'}`}>
          {status || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Recomendación IA',
      dataIndex: 'ai_recommendation',
      key: 'ai_recommendation',
      width: 120,
      render: (rec: string) => (
        <span className={`status-${rec?.toLowerCase() || 'unknown'}`}>
          {rec || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Confianza IA',
      dataIndex: 'ai_confidence',
      key: 'ai_confidence',
      width: 100,
      render: (confidence: number) => `${(confidence * 100).toFixed(1)}%`,
    },
    {
      title: 'Tipo de Desacuerdo',
      dataIndex: 'disagreement_type',
      key: 'disagreement_type',
      width: 150,
      render: (type: string) => {
        if (type === 'ai_more_conservative') return 'IA más conservadora'
        if (type === 'ai_more_liberal') return 'IA más liberal'
        return 'Mixto'
      },
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('es-ES'),
    },
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <Title level={2} style={{ margin: 0 }}>Análisis Avanzado</Title>
        <Select
          value={selectedPeriod}
          onChange={setSelectedPeriod}
          style={{ width: isMobile ? '100%' : 180, maxWidth: 240 }}
        >
          <Option value={7}>Últimos 7 días</Option>
          <Option value={30}>Últimos 30 días</Option>
          <Option value={90}>Últimos 90 días</Option>
        </Select>
      </div>

      {/* Comparación IA vs Reglas */}
      {aiVsRules && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Casos Analizados"
                  value={aiVsRules.summary.total_analyzed}
                  prefix={<RobotOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Tasa de Acuerdo"
                  value={aiVsRules.summary.agreement_rate}
                  suffix="%"
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="IA Más Conservadora"
                  value={aiVsRules.breakdown.ai_more_conservative}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<AlertOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="IA Más Liberal"
                  value={aiVsRules.breakdown.ai_more_liberal}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Comparativo: IA vs Reglas de Negocio" style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Text strong>Distribución de Acuerdos y Desacuerdos</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                      {aiVsRules.breakdown.agreements}
                    </div>
                    <Text type="secondary">Acuerdos</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>
                      {aiVsRules.breakdown.disagreements}
                    </div>
                    <Text type="secondary">Desacuerdos</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Text strong>Tipos de Desacuerdo</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#faad14' }}>
                      {aiVsRules.breakdown.ai_more_conservative}
                    </div>
                    <Text type="secondary">IA Conservadora</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                      {aiVsRules.breakdown.ai_more_liberal}
                    </div>
                    <Text type="secondary">IA Liberal</Text>
                  </div>
                </div>
              </Col>
            </Row>

            {aiVsRules.cases.filter(c => !c.agreement).length > 0 ? (
              <div style={{ marginTop: 24 }}>
                <Title level={4}>Casos con Desacuerdo</Title>
                <Table
                  columns={disagreementColumns}
                  dataSource={aiVsRules.cases.filter(c => !c.agreement)}
                  rowKey="request_id"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: isMobile ? 900 : 720 }}
                  size="small"
                />
              </div>
            ) : (
              <Alert
                message="¡Excelente!"
                description="No hay desacuerdos entre la IA y las reglas de negocio en el período seleccionado."
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </>
      )}

      {/* Rendimiento de Validaciones */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14}>
          <Card title="Rendimiento por Paso de Validación">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={validationChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="step" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="passed" stackId="a" fill="#52c41a" name="Pasados" />
                <Bar dataKey="failed" stackId="a" fill="#ff4d4f" name="Fallados" />
                <Bar dataKey="warning" stackId="a" fill="#faad14" name="Advertencias" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card title="Tasa de Éxito por Validación">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={validationChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="step" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={9}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => [`${value}%`, 'Tasa de Éxito']} />
                <Line 
                  type="monotone" 
                  dataKey="passRate" 
                  stroke="#1890ff" 
                  strokeWidth={2}
                  dot={{ fill: '#1890ff', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Principales Razones de Rechazo */}
      <Card title="Principales Razones de Rechazo">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rejectionReasons.slice(0, 8)} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="reason" 
              width={200}
              fontSize={10}
            />
            <Tooltip />
            <Bar dataKey="count" fill="#ff4d4f" />
          </BarChart>
        </ResponsiveContainer>
        
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {rejectionReasons.slice(0, 6).map((reason, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
              <Text ellipsis style={{ maxWidth: 200 }}>{reason.reason}</Text>
              <Text strong>{reason.count} ({reason.percentage}%)</Text>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default Analytics